import { faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import { useDebounce } from 'helpers/utils';
import { useEffect, useRef, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ContactsService from 'services/Contacts.service';
import CustomerContactsService from 'services/CustomerContacts.service';

/**
 *
 * Componente de busca de contatos em formulários.
 * @param {string} label - Label do componente.
 * @param {boolean} required - Se o campo é obrigatório.
 * @param {number} customerId - ID do cliente (opcional). Se não for informado, a busca será feita em todos os contatos.
 * @param {function} onChange - Função de callback para o onChange.
 */
const ContactSearchComponent = ({
  label = 'Cc',
  required = false,
  customerId,
  onChange,
  value = ''
}) => {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const suggestionRef = useRef(null);

  useEffect(() => {
    if (selectedEmails)
      onChange(selectedEmails.map(email => email.address).join('; '));
  }, [selectedEmails]); // Call onChange when selectedEmails changes

  useEffect(() => {
    if (value) {
      setInputValue(value);
      const emailsFromValue = value
        .split('; ')
        .filter(email => email.trim() !== '');

      // Only update selectedEmails if the value is different from current selectedEmails
      setSelectedEmails(prevSelectedEmails => {
        const currentEmails = prevSelectedEmails
          .map(email => email.address)
          .join('; ');
        if (currentEmails !== value) {
          return emailsFromValue.map(email => ({
            address: email,
            name: email,
            id: Date.now() + Math.random() // Use Math.random() to ensure unique IDs
          }));
        }
        return prevSelectedEmails;
      });
    }
  }, [value]);

  // Search contacts based on input
  const searchContacts = async query => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    if (customerId) {
      const queryParam = new URLSearchParams({ searchTerm: query }).toString();
      const response = await CustomerContactsService.searchByNameAndEmail(
        customerId,
        queryParam
      );
      if (response && Array.isArray(response)) setSearchResults(response);
    } else {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        name: query,
        includeInactives: false
      }).toString();
      const response = await ContactsService.list(queryParams);
      if (response && response.content) setSearchResults(response.content);
    }
  };

  const debouncedSearchContacts = useDebounce(searchContacts, 300);

  // Handle input change
  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);

    // Get the last part after semicolon (current typing)
    const parts = value.split(';');
    const currentInput = parts[parts.length - 1].trim();

    debouncedSearchContacts(currentInput);
    setShowSuggestions(currentInput.length > 0);
  };

  // Handle contact selection
  const handleContactSelect = contact => {
    const parts = inputValue.split(';');
    parts[parts.length - 1] = contact.email;
    const newValue = parts.join('; ') + '; ';

    setInputValue(newValue);
    setShowSuggestions(false);
    setSearchResults([]);

    // Add to selected emails if not already present
    if (!selectedEmails.some(email => email.address === contact.email)) {
      setSelectedEmails([
        ...selectedEmails,
        {
          address: contact.email,
          name: contact.name,
          id: contact.id
        }
      ]);
    }
  };

  // Handle adding custom email on Enter or semicolon
  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ';') {
      e.preventDefault();

      const parts = inputValue.split(';');
      const currentInput = parts[parts.length - 1].trim();

      if (currentInput && isValidEmail(currentInput)) {
        // Add custom email
        const newValue = inputValue.includes(currentInput)
          ? inputValue + '; '
          : inputValue + currentInput + '; ';
        setInputValue(newValue);

        // Add to selected emails
        if (!selectedEmails.some(email => email.address === currentInput)) {
          setSelectedEmails([
            ...selectedEmails,
            {
              address: currentInput,
              name: currentInput,
              id: Date.now()
            }
          ]);
        }
      }

      setShowSuggestions(false);
      setSearchResults([]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Email validation
  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Remove email from selected list
  const removeEmail = emailToRemove => {
    const updatedEmails = selectedEmails.filter(
      email => email.address !== emailToRemove.address
    );
    setSelectedEmails(updatedEmails);

    // Update input value
    const emailAddresses = updatedEmails.map(email => email.address);
    const newValue =
      emailAddresses.length > 0 ? emailAddresses.join('; ') + '; ' : '';
    setInputValue(newValue);
  };

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="mb-2 mt-2">
      <Flex alignItems="center" className="mb-1">
        <Form.Label className="mb-0 me-1">
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        <div className="position-relative">
          <OverlayTrigger
            overlay={
              <Tooltip>
                Digite o nome ou o e-mail do contato para pesquisar. Caso o
                contato não esteja cadatrado, basta digitar o e-mail. Separe os
                e-mails por ponto e vírgula para enviar para mais de uma pessoa
              </Tooltip>
            }
            style={{ cursor: 'help' }}
          >
            <span>
              <FontAwesomeIcon icon={faInfoCircle} size="sm" />
            </span>
          </OverlayTrigger>
        </div>
      </Flex>

      <div className="position-relative">
        <Form.Control
          ref={inputRef}
          type="text"
          name="sdf242"
          className="form-control"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Digite o nome ou email do contato..."
          style={{ paddingRight: '12px' }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          required={required}
        />

        {required && (
          <Form.Control.Feedback type="invalid">
            Este campo é obrigatório.
          </Form.Control.Feedback>
        )}

        {/* Suggestions dropdown */}
        {showSuggestions && searchResults.length > 0 && (
          <div
            ref={suggestionRef}
            className="position-absolute w-100 bg-100 border border-top-0 shadow-sm"
            style={{
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto',
              top: '100%'
            }}
          >
            {searchResults.map(contact => (
              <div
                key={contact.id}
                className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                onClick={() => handleContactSelect(contact)}
                style={{
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                <div className="fw-bold">{contact.name}</div>
                <div className="text-muted small">{contact.email}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected emails display */}
      {selectedEmails.length > 0 && (
        <div className="mt-2">
          <div className="d-flex flex-wrap gap-1">
            {selectedEmails.map(email => (
              <span
                key={email.id}
                className="badge bg-primary d-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem' }}
              >
                <span>
                  {email.name !== email.address
                    ? `${email.name} (${email.address})`
                    : email.address}
                </span>
                <FontAwesomeIcon
                  icon={faTimes}
                  size="xs"
                  className="cursor-pointer"
                  onClick={() => removeEmail(email)}
                  style={{ cursor: 'pointer' }}
                />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSearchComponent;
